// ─────────────────────────────────────────────────────────────────
// Glass-i — Company Defaults Master page (Stage 6CM.1 · redesign 6CM.5).
//
// Central place for company-level default setup values. Becomes the
// primary effective-default source for NEW Proforma documents; Proforma
// Settings is retained as the compatibility fallback (existing drafts and
// saved Proformas are never modified).
//
// Layout — a measured "spec sheet": a sticky numbered INDEX rail (left,
// jump-nav + scroll-spy + a tape-measure tick motif that nods to the
// glass-cutting trade), the unified settings sheet (centre) where the
// Company Identity letterhead is section 01 and every defaults group is a
// numbered section, and a sticky "proforma preview" + actions rail (right)
// that mirrors the form in plain language. Price/Selling UOM + Input Unit
// options come from the dropdown endpoint (UOM Master) — never hardcoded.
// Only internal settings enums fall back to in-file lists when the
// dropdowns endpoint fails. Theme tokens are owned by the global
// ConfigProvider — Tailwind + companyDefaults.css only do layout/motifs.
// ─────────────────────────────────────────────────────────────────

import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Card, Form, Select, InputNumber, Switch, Button, Alert, Spin, Tag, message
} from 'antd';
import {
  SaveOutlined, ReloadOutlined, UndoOutlined, HistoryOutlined, SettingOutlined,
  ColumnWidthOutlined, ScissorOutlined, BorderOuterOutlined, PercentageOutlined,
  RocketOutlined, SafetyCertificateOutlined, LockOutlined,
  IdcardOutlined
} from '@ant-design/icons';
import { companyDefaultsApi, loadCompanyDefaultsDropdownsSafe } from './companyDefaultsApi';
import {
  hydrateCompanyDefaults, buildCompanyDefaultsPayload, validateCompanyDefaults, CHARGEABLE_MODE_HELP,
  EDGEWORK_BASIS_HELP, APPROVAL_LEVEL_HELP, areaModeFromUom
} from './companyDefaultsHelpers';
import CompanyDefaultsEventsDrawer from './CompanyDefaultsEventsDrawer';
import CompanyIdentityCard from './CompanyIdentityCard';
import './companyDefaults.css';

// Internal settings-enum fallbacks (NOT business UOMs) — used only if the
// dropdowns endpoint fails. Price/Input UOM lists are NEVER hardcoded here.
const FB = {
  wastageModes: [
    { value: 'ROUND_OFF', label: 'Round-off (inch step)' }, { value: 'PLUS_MM', label: '(+) MM' },
    { value: 'EVEN_IN', label: 'Even inch' }, { value: 'EVEN_CM', label: 'Even cm' },
    { value: 'EVEN_FT', label: 'Even ft' }
  ],
  chargeableModes: [
    { value: 'AUTO_FROM_WASTAGE', label: 'Auto from wastage' },
    { value: 'MANUAL_EDIT_ALLOWED', label: 'Manual edit allowed' },
    { value: 'LOCKED_TO_ACTUAL', label: 'Locked to actual' },
    { value: 'LOCKED_TO_WASTAGE', label: 'Locked to wastage' }
  ],
  areaModes: [
    { value: 'SQFT', label: 'SQFT' }, { value: 'SQM', label: 'SQM' },
    { value: 'BOTH', label: 'Both (SQFT + SQM)' }
  ],
  roundOffModes: [
    { value: 'NONE', label: 'None' }, { value: 'NEAREST', label: 'Nearest' },
    { value: 'UP', label: 'Round up' }, { value: 'DOWN', label: 'Round down' }
  ],
  taxSources: [
    { value: 'ITEM_MASTER', label: 'Item Master' }, { value: 'MANUAL', label: 'Manual' },
    { value: 'NON_TAXABLE', label: 'Non-taxable' }
  ],
  // Dimension-entry units (not billing UOMs).
  inputUnits: [
    { value: 'MM', label: 'MM' }, { value: 'IN', label: 'IN' },
    { value: 'CM', label: 'CM' }, { value: 'FT', label: 'FT' }
  ],
  // Edgework (RFT/RMT per-side) length basis — fixed enum, not a UOM list.
  edgeworkBases: [
    { value: 'CHARGE', label: 'Charge size (post-wastage)' },
    { value: 'ACTUAL', label: 'Actual entered size' }
  ]
};

// Round-off inch steps — mirrors the Proforma controls panel WASTAGE_STEPS so a
// ROUND_OFF default offers the same 0,3,6,9… choices the editor uses.
const WASTAGE_STEPS = [0, 3, 6, 9, 12, 15, 18, 21, 24];

// The index/section order — single source of truth for the jump-nav rail and
// the scroll-spy. `code` is the mono field-code shown on each header.
const SECTIONS = [
  { id: 'sec-identity',    num: '01', code: 'IDENTITY',    label: 'Letterhead',    icon: <IdcardOutlined /> },
  { id: 'sec-units',       num: '02', code: 'UNITS',       label: 'Selling units', icon: <ColumnWidthOutlined /> },
  { id: 'sec-wastage',     num: '03', code: 'WASTAGE',     label: 'Wastage',       icon: <ScissorOutlined /> },
  { id: 'sec-chargeable',  num: '04', code: 'CHARGEABLE',  label: 'Chargeable',    icon: <BorderOuterOutlined /> },
  { id: 'sec-tax',         num: '05', code: 'CHARGES+TAX', label: 'Charges & tax', icon: <PercentageOutlined /> },
  { id: 'sec-rollout',     num: '06', code: 'ROLLOUT',     label: 'Rollout',       icon: <RocketOutlined /> },
  { id: 'sec-approvals',   num: '07', code: 'APPROVALS',   label: 'Approvals',     icon: <SafetyCertificateOutlined /> },
  { id: 'sec-compat',      num: '08', code: 'COMPAT',      label: 'Compatibility', icon: <SafetyCertificateOutlined /> }
];

// Which section owns each form field — lets a save-time validation error jump the
// navigator straight to the panel that holds the offending input.
const FIELD_SECTION = {
  defaultPriceUomCode: 'sec-units', defaultAreaMode: 'sec-units',
  defaultInputUnitCode: 'sec-units', defaultCompanyStateCode: 'sec-units',
  defaultWastageMode: 'sec-wastage', defaultWastageWidthMm: 'sec-wastage', defaultWastageHeightMm: 'sec-wastage',
  defaultChargeableMode: 'sec-chargeable', defaultEditChargeable: 'sec-chargeable', edgeworkDimensionBasis: 'sec-chargeable',
  defaultTaxSource: 'sec-tax', defaultRoundOffMode: 'sec-tax', autoApplyOtherCharges: 'sec-tax',
  allowRuntimeOtherChargeOverride: 'sec-tax', defaultRoundOffEnabled: 'sec-tax',
  applyToNewProforma: 'sec-rollout', applyToExistingDrafts: 'sec-rollout',
  proformaApprovalLevel: 'sec-approvals'
};

const num = (v) => Number(v) || 0;
const labelOf = (opts, val, fb) => {
  const hit = (Array.isArray(opts) ? opts : []).find((o) => o.value === val);
  return (hit && hit.label) || fb || (val != null && val !== '' ? String(val) : '—');
};

// Numbered section header — number chip, mono field-code, title, purpose line.
// Sections are told apart by number + code + icon, keeping the accent quiet.
function SectionHead({ num: n, code, icon, title, desc }) {
  return (
    <div className="cd-sec-head">
      <span className="cd-sec-num">{n}</span>
      <span className="grid place-items-center w-7 h-7 rounded-md bg-brand-50 text-brand-600 shrink-0 text-[14px]">{icon}</span>
      <div className="cd-sec-titles">
        <div className="cd-sec-code">{code}</div>
        <div className="cd-sec-title">{title}</div>
        {desc ? <div className="cd-sec-desc">{desc}</div> : null}
      </div>
    </div>
  );
}

// A settings section card with a numbered header (identity reuses the same
// header via CompanyIdentityCard for a consistent sheet rhythm).
function Section({ id, active = true, num: n, code, icon, title, desc, children }) {
  // Always mounted; hidden when not the active tab so the whole Form stays
  // registered (validation, watched conditionals and the save payload are
  // identical to a single-scroll layout).
  return (
    <div hidden={!active}>
      <Card size="small" id={id} className="shadow-card cd-section" styles={{ body: { padding: 0 } }}>
        <SectionHead num={n} code={code} icon={icon} title={title} desc={desc} />
        <div className="px-4 py-4">{children}</div>
      </Card>
    </div>
  );
}

// Inline label + switch — reads better than a stacked Form.Item for booleans.
function SwitchRow({ name, label, help }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2 border-b border-surface-100 last:border-0">
      <div className="min-w-0">
        <div className="text-[13px] text-ink-800">{label}</div>
        {help ? <div className="text-[11.5px] text-ink-400 mt-0.5 leading-snug">{help}</div> : null}
      </div>
      <Form.Item name={name} valuePropName="checked" noStyle>
        <Switch />
      </Form.Item>
    </div>
  );
}

export default function CompanyDefaultsPage() {
  const [form] = Form.useForm();
  const identityRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [bootError, setBootError] = useState(null);
  const [activeSection, setActiveSection] = useState('sec-identity');
  const [dd, setDd] = useState({
    priceUoms: [], states: [], inputUnits: FB.inputUnits, areaModes: FB.areaModes,
    wastageModes: FB.wastageModes, chargeableModes: FB.chargeableModes,
    roundOffModes: FB.roundOffModes, taxSources: FB.taxSources,
    edgeworkBases: FB.edgeworkBases
  });
  const [ddLoading, setDdLoading] = useState(false);
  const [eventsOpen, setEventsOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  // Watched values drive both the conditional inputs and the live preview rail.
  const chargeableMode = Form.useWatch('defaultChargeableMode', form);
  // ROUND_OFF collects an inch step (0,3,6,9…); PLUS_MM a free mm value; the
  // EVEN_* modes need no step. Mirrors the Proforma controls panel exactly.
  const wastageMode = Form.useWatch('defaultWastageMode', form);
  const isRoundOff = String(wastageMode || 'ROUND_OFF') === 'ROUND_OFF';
  const isPlusMm = wastageMode === 'PLUS_MM';
  const wW = Form.useWatch('defaultWastageWidthMm', form);
  const wH = Form.useWatch('defaultWastageHeightMm', form);
  const inputUnit = Form.useWatch('defaultInputUnitCode', form);
  const edgeworkBasis = Form.useWatch('edgeworkDimensionBasis', form);
  const taxSource = Form.useWatch('defaultTaxSource', form);
  const applyNew = Form.useWatch('applyToNewProforma', form);
  const approvalLevel = Form.useWatch('proformaApprovalLevel', form);
  // STRICT Price-UOM rule: the Area Mode follows the selling Price UOM and is
  // read-only — SQM ⇒ SQM, SQF ⇒ SQFT. They can never diverge.
  const priceUomCode = Form.useWatch('defaultPriceUomCode', form);
  useEffect(() => {
    if (priceUomCode) form.setFieldsValue({ defaultAreaMode: areaModeFromUom(priceUomCode) });
  }, [priceUomCode, form]);

  // Tab-style navigation: picking a section swaps the focused panel and scrolls
  // the configurator back to the top so the chosen panel starts in view.
  const configTopRef = useRef(null);
  const selectSection = (id) => {
    setActiveSection(id);
    const el = configTopRef.current;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  let wastageText;
  if (wastageMode === 'PLUS_MM') wastageText = `(+) ${num(wW)} × ${num(wH)} mm`;
  else if (isRoundOff) wastageText = `Round-off · ${num(wW)} / ${num(wH)} in`;
  else wastageText = labelOf(dd.wastageModes, wastageMode, 'Wastage');

  // Short current-value hint shown under each row in the section navigator.
  const navHints = {
    'sec-identity':   'Printed on documents',
    'sec-units':      `${priceUomCode || 'Set UOM'}${inputUnit ? ' · ' + inputUnit : ''}`,
    'sec-wastage':    wastageText,
    'sec-chargeable': labelOf(dd.chargeableModes, chargeableMode),
    'sec-tax':        labelOf(dd.taxSources, taxSource),
    'sec-rollout':    applyNew ? 'On for new proformas' : 'Not applied',
    'sec-approvals':  approvalLevel === 'L1_L2' ? 'Accounts + Mgmt' : (approvalLevel === 'NONE' ? 'No approvals' : '—'),
    'sec-compat':     'Seeded · fallback kept'
  };

  const hydrate = useCallback((dto) => {
    form.setFieldsValue(hydrateCompanyDefaults(dto));
    setDirty(false);
  }, [form]);

  const load = useCallback(async () => {
    setLoading(true);
    setDdLoading(true);
    setBootError(null);
    try {
      const [settings, drops] = await Promise.all([
        companyDefaultsApi.get(),
        loadCompanyDefaultsDropdownsSafe()
      ]);
      if (drops) {
        setDd({
          priceUoms:       Array.isArray(drops.priceUoms) ? drops.priceUoms : [],
          states:          Array.isArray(drops.states) ? drops.states : [],
          inputUnits:      drops.inputUnits?.length ? drops.inputUnits : FB.inputUnits,
          areaModes:       drops.areaModes?.length ? drops.areaModes : FB.areaModes,
          wastageModes:    drops.wastageModes?.length ? drops.wastageModes : FB.wastageModes,
          chargeableModes: drops.chargeableModes?.length ? drops.chargeableModes : FB.chargeableModes,
          roundOffModes:   drops.roundOffModes?.length ? drops.roundOffModes : FB.roundOffModes,
          taxSources:      drops.taxSources?.length ? drops.taxSources : FB.taxSources,
          edgeworkBases:   FB.edgeworkBases
        });
      }
      hydrate(settings || {});
    } catch (e) {
      setBootError(e?.response?.data?.error?.message || e.message || 'Failed to load company defaults.');
    } finally {
      setLoading(false);
      setDdLoading(false);
    }
  }, [hydrate]);

  useEffect(() => { load(); }, [load]);

  const onSave = async () => {
    let values;
    try { values = await form.validateFields(); }
    catch (err) {
      const bad = err?.errorFields?.[0]?.name?.[0];
      if (bad && FIELD_SECTION[bad]) setActiveSection(FIELD_SECTION[bad]);
      return;
    }
    const check = validateCompanyDefaults(values);
    if (!check.ok) { message.error(check.errors[0].message); return; }
    setSaving(true);
    try {
      const saved = await companyDefaultsApi.update(buildCompanyDefaultsPayload(values));
      hydrate(saved || values);
      message.success('Company defaults saved. New proformas will use them.');
    } catch (e) {
      message.error(e?.response?.data?.error?.message || e.message || 'Failed to save company defaults.');
    } finally {
      setSaving(false);
    }
  };

  const onReset = async () => {
    setResetting(true);
    try {
      const saved = await companyDefaultsApi.reset();
      hydrate(saved || {});
      message.success('Company defaults reset to safe defaults.');
    } catch (e) {
      message.error(e?.response?.data?.error?.message || e.message || 'Failed to reset company defaults.');
    } finally {
      setResetting(false);
    }
  };

  const openHistory = async () => {
    setEventsOpen(true);
    setEventsLoading(true);
    try {
      const list = await companyDefaultsApi.getEvents({ limit: 50 });
      setEvents(Array.isArray(list) ? list : []);
    } catch (_e) {
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  };

  const busy = saving || resetting;

  return (
    <div className="p-4 max-w-[1180px] mx-auto">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-11 h-11 rounded-xl bg-brand-gradient text-white text-[20px] shadow-card">
            <SettingOutlined />
          </span>
          <div>
            <div className="text-[22px] font-semibold text-ink-900 leading-tight">Company Defaults</div>
            <div className="text-[13px] text-ink-500 mt-0.5">
              Configure how every new proforma is born — letterhead, units, wastage, charges, tax and rollout.
            </div>
          </div>
        </div>
        <Button icon={<HistoryOutlined />} onClick={openHistory} disabled={busy}>
          Change history
        </Button>
      </div>

      {bootError ? (
        <Alert type="error" message="Couldn’t load company defaults" description={bootError}
          className="mb-4" showIcon closable onClose={() => setBootError(null)} />
      ) : null}

      <div className="cd-config" ref={configTopRef}>
        {/* ── Left: section navigator (each row shows its current value) ── */}
        <aside className="cd-nav">
          <div className="cd-nav-cap">SECTIONS</div>
          {SECTIONS.map((s) => (
            <button
              type="button"
              key={s.id}
              className={`cd-nrow${activeSection === s.id ? ' is-active' : ''}`}
              onClick={() => selectSection(s.id)}
              aria-current={activeSection === s.id ? 'true' : undefined}
            >
              <span className="cd-nrow-num">{s.num}</span>
              <span className="cd-nrow-ic">{s.icon}</span>
              <span className="cd-nrow-tx">
                <span className="cd-nrow-lb">{s.label}</span>
                <span className="cd-nrow-hint">{navHints[s.id]}</span>
              </span>
            </button>
          ))}
          <div className="cd-nav-foot">Set once — stamped on every new proforma. Letterhead has its own save.</div>
        </aside>

        {/* ── Right: focused panel + sticky save bar ── */}
        <div className="cd-panelcol">

          {/* 01 — Company identity / letterhead (self-contained form + save). */}
          <div hidden={activeSection !== 'sec-identity'}>
            <CompanyIdentityCard
              ref={identityRef}
              head={<SectionHead num="01" code="IDENTITY" icon={<IdcardOutlined />}
                title="Company identity"
                desc="The “from” letterhead on printed documents (RFQ, etc.). Saves on its own. Leave blank to omit." />}
            />
          </div>

          <div hidden={activeSection === 'sec-identity'}>
          <Spin spinning={loading}>
            <Form form={form} layout="vertical" requiredMark={false}
              onValuesChange={() => setDirty(true)} className="flex flex-col gap-4">

              <Section id="sec-units" active={activeSection === 'sec-units'} num="02" code="UNITS" icon={<ColumnWidthOutlined />}
                title="Selling units & identity"
                desc="The UOM the whole application bills in, plus how dimensions are entered.">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                  <Form.Item label="Selling / Price UOM" name="defaultPriceUomCode"
                    rules={[{ required: true, message: 'Price UOM is mandatory.' }]}
                    tooltip="The whole application works in this UOM. SQM ⇒ all areas + pricing in square metres; SQF ⇒ square feet.">
                    <Select options={dd.priceUoms} loading={ddLoading} placeholder="From UOM Master" />
                  </Form.Item>
                  <Form.Item label="Area Mode" name="defaultAreaMode"
                    tooltip="Locked to the selling Price UOM — the application stays strictly single-UOM.">
                    <Select options={dd.areaModes} disabled
                      suffixIcon={<LockOutlined className="text-ink-400" />} />
                  </Form.Item>
                  <Form.Item label="Input Unit" name="defaultInputUnitCode"
                    tooltip="Unit used to type Width/Height in the editor — independent of the billing UOM.">
                    <Select allowClear options={dd.inputUnits} placeholder="MM / IN / CM / FT" />
                  </Form.Item>
                  <Form.Item label="Company State (Co. State)" name="defaultCompanyStateCode"
                    tooltip="Seller state seeded onto every new Proforma — drives the GST split. Pick once here.">
                    <Select allowClear showSearch optionFilterProp="label" options={dd.states}
                      loading={ddLoading} placeholder="Select company state" />
                  </Form.Item>
                </div>
                <Alert type="info" showIcon className="mt-1"
                  icon={<LockOutlined />}
                  message={<span className="text-[12.5px]">Area Mode follows the Price UOM automatically — the whole app stays in one unit.</span>} />
              </Section>

              <Section id="sec-wastage" active={activeSection === 'sec-wastage'} num="03" code="WASTAGE" icon={<ScissorOutlined />}
                title="Wastage"
                desc="Extra added to the actual size to get the chargeable (billed) size.">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4">
                  <Form.Item label="Wastage Mode" name="defaultWastageMode">
                    <Select options={dd.wastageModes} />
                  </Form.Item>
                  <Form.Item label={isRoundOff ? 'Width (inch step)' : 'Width (mm)'} name="defaultWastageWidthMm">
                    {isRoundOff
                      ? <Select className="w-full" options={WASTAGE_STEPS.map((v) => ({ label: String(v), value: v }))} />
                      : <InputNumber min={0} className="w-full" disabled={!isPlusMm} />}
                  </Form.Item>
                  <Form.Item label={isRoundOff ? 'Height (inch step)' : 'Height (mm)'} name="defaultWastageHeightMm">
                    {isRoundOff
                      ? <Select className="w-full" options={WASTAGE_STEPS.map((v) => ({ label: String(v), value: v }))} />
                      : <InputNumber min={0} className="w-full" disabled={!isPlusMm} />}
                  </Form.Item>
                </div>
              </Section>

              <Section id="sec-chargeable" active={activeSection === 'sec-chargeable'} num="04" code="CHARGEABLE" icon={<BorderOuterOutlined />}
                title="Chargeable dimensions"
                desc="How the billed Width/Height is derived from the actual size.">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 items-start">
                  <Form.Item label="Chargeable Mode" name="defaultChargeableMode">
                    <Select options={dd.chargeableModes} />
                  </Form.Item>
                  <div className="sm:pt-1">
                    <SwitchRow name="defaultEditChargeable" label="Edit chargeable by default"
                      help="Unlock the CHRG W/H cells on new proformas." />
                  </div>
                </div>
                <Alert type="info" showIcon className="mt-1"
                  message={<span className="text-[12.5px]">{CHARGEABLE_MODE_HELP[chargeableMode] || 'Choose how chargeable W/H is derived.'}</span>} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 items-start mt-3">
                  <Form.Item label="Edgework RFT/RMT basis" name="edgeworkDimensionBasis">
                    <Select options={dd.edgeworkBases} />
                  </Form.Item>
                </div>
                <Alert type="info" showIcon className="mt-1"
                  message={<span className="text-[12.5px]">{EDGEWORK_BASIS_HELP[edgeworkBasis] || 'Which size the per-side RFT/RMT edge length is measured on.'}</span>} />
              </Section>

              <Section id="sec-tax" active={activeSection === 'sec-tax'} num="05" code="CHARGES+TAX" icon={<PercentageOutlined />}
                title="Charges & tax"
                desc="Defaults for other charges, the tax source and total rounding.">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                  <Form.Item label="Tax Source" name="defaultTaxSource">
                    <Select options={dd.taxSources} />
                  </Form.Item>
                  <Form.Item label="Round-off Mode" name="defaultRoundOffMode">
                    <Select options={dd.roundOffModes} />
                  </Form.Item>
                </div>
                <div className="mt-1">
                  <SwitchRow name="autoApplyOtherCharges" label="Auto-apply other charges"
                    help="Seed master other-charge rules onto each new proforma." />
                  <SwitchRow name="allowRuntimeOtherChargeOverride" label="Allow runtime override"
                    help="Let users adjust other-charge values inside a proforma." />
                  <SwitchRow name="defaultRoundOffEnabled" label="Round off the grand total"
                    help="Apply the round-off mode to the document total." />
                </div>
              </Section>

              <Section id="sec-rollout" active={activeSection === 'sec-rollout'} num="06" code="ROLLOUT" icon={<RocketOutlined />}
                title="Rollout"
                desc="Where these defaults take effect.">
                <SwitchRow name="applyToNewProforma" label="Apply to new proformas"
                  help="Use these defaults whenever a proforma is created." />
                <SwitchRow name="applyToExistingDrafts" label="Apply to existing drafts"
                  help="Recorded for audit only — existing drafts are not changed yet." />
                <Alert type="warning" showIcon className="mt-3"
                  message={<span className="text-[12.5px]">“Apply to existing drafts” is recorded but does not retroactively modify existing drafts or saved proformas in this stage.</span>} />
              </Section>

              <Section id="sec-approvals" active={activeSection === 'sec-approvals'} num="07" code="APPROVALS" icon={<SafetyCertificateOutlined />}
                title="Proforma approvals"
                desc="How many sign-offs a quotation needs before a Work Order can be released.">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                  <Form.Item label="Approval level" name="proformaApprovalLevel">
                    <Select
                      options={[
                        { value: 'NONE', label: 'None — Work Order ready as soon as you confirm' },
                        { value: 'L1_L2', label: 'Accounts review + optional Management Approval' }
                      ]} />
                  </Form.Item>
                </div>
                <Alert type="info" showIcon className="mt-1"
                  message={<span className="text-[12.5px]">{APPROVAL_LEVEL_HELP[approvalLevel] || 'Choose how many approvals a proforma needs.'}</span>} />
              </Section>

              <Section id="sec-compat" active={activeSection === 'sec-compat'} num="08" code="COMPAT" icon={<SafetyCertificateOutlined />}
                title="Compatibility"
                desc="How this master relates to the older Proforma Settings.">
                <div className="flex flex-col gap-2 text-[12.5px] text-ink-600">
                  <div><Tag color="purple">Seeded</Tag> Existing Proforma Settings were copied in on first run.</div>
                  <div><Tag color="blue">Fallback</Tag> Proforma Settings are kept as the fallback — nothing was deleted.</div>
                  <div><Tag color="green">Safe</Tag> Existing drafts and saved proformas are never modified.</div>
                </div>
              </Section>
            </Form>
          </Spin>
          </div>

          {/* Sticky save bar — commits the defaults form. The letterhead (01)
              keeps its own inline Save, so the bar is hidden on that panel. */}
          {activeSection !== 'sec-identity' ? (
            <div className="cd-savebar">
              {dirty
                ? <span className="cd-savebar-status is-dirty"><span className="d" />Unsaved changes</span>
                : <span className="cd-savebar-status">All changes saved</span>}
              <div className="cd-savebar-actions">
                <Button icon={<ReloadOutlined />} onClick={load} disabled={busy}>Refresh</Button>
                <Button icon={<UndoOutlined />} onClick={onReset} loading={resetting} disabled={saving}>Reset</Button>
                <Button type="primary" icon={<SaveOutlined />} loading={saving}
                  disabled={loading || ddLoading} onClick={onSave}>
                  Save changes
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <CompanyDefaultsEventsDrawer
        open={eventsOpen}
        onClose={() => setEventsOpen(false)}
        events={events}
        loading={eventsLoading}
      />
    </div>
  );
}
